// Serviço para gerenciar índices econômicos
// Integra dados dinâmicos do Supabase com fallback para arquivo estático
import { supabase } from "@/integrations/supabase/client";

export interface EconomicIndex {
  id: number;
  series_code: number;
  date: string;
  value: number;
  source: string;
  created_at: string;
}

export interface IndexPoint {
  date: string;
  value: number;
}

export interface SeriesInfo {
  code: number;
  name: string;
  type: 'monthly' | 'daily';
  unit: string;
  description: string;
}

// Definições das séries econômicas - 11 séries BCB completas
export const ECONOMIC_SERIES: Record<number, SeriesInfo> = {
  // SELIC (3 variações)
  11: {
    code: 11,
    name: 'SELIC Over',
    type: 'daily',
    unit: '% a.d.',
    description: 'Taxa SELIC Over diária (base 252 dias)'
  },
  432: {
    code: 432,
    name: 'SELIC Meta',
    type: 'monthly', // Meta é definida por reunião, exibimos como monthly
    unit: '% a.a.',
    description: 'Taxa SELIC Meta (definida pelo Copom)'
  },
  4390: {
    code: 4390,
    name: 'SELIC Mensal',
    type: 'monthly',
    unit: '% a.m.',
    description: 'Taxa SELIC acumulada no mês'
  },
  
  // CDI (2 variações)
  12: {
    code: 12,
    name: 'CDI',
    type: 'daily',
    unit: '% a.d.',
    description: 'Certificado de Depósito Interbancário (diário)'
  },
  4391: {
    code: 4391,
    name: 'CDI Mensal',
    type: 'monthly',
    unit: '% a.m.',
    description: 'CDI acumulado no mês'
  },
  
  // IPCA (2 variações)
  433: {
    code: 433,
    name: 'IPCA',
    type: 'monthly',
    unit: '% a.m.',
    description: 'Índice Nacional de Preços ao Consumidor Amplo'
  },
  10764: {
    code: 10764,
    name: 'IPCA-E',
    type: 'monthly',
    unit: '%',
    description: 'IPCA Especial (para cálculos trabalhistas pré-ajuizamento)'
  },
  
  // Outros índices
  188: {
    code: 188,
    name: 'INPC',
    type: 'monthly',
    unit: '% a.m.',
    description: 'Índice Nacional de Preços ao Consumidor'
  },
  189: {
    code: 189,
    name: 'IGP-M',
    type: 'monthly',
    unit: '% a.m.',
    description: 'Índice Geral de Preços do Mercado'
  },
  226: {
    code: 226,
    name: 'TR',
    type: 'monthly',
    unit: '% a.m.',
    description: 'Taxa Referencial'
  }
};

// Cache simples em memória para otimizar performance
const cache = new Map<string, { data: any; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutos

/**
 * Guard para normalizar SELIC Mensal (SGS 4390) - evita regressão de divisões inadequadas
 * A 4390 normalmente fica entre ~0.6% e ~1.5% a.m.
 * Se chegar algo < 0.5, provavelmente dividiram por 100 por engano.
 */
export function normalizeMonthlyPct4390(v?: number | null): number | null {
  if (v == null || Number.isNaN(v)) return v ?? null;
  return v > 0 && v < 0.5 ? v * 100 : v;
}

// Função auxiliar para cache
function getCacheKey(operation: string, params?: any): string {
  return `${operation}_${JSON.stringify(params || {})}`;
}

function setCache(key: string, data: any): void {
  cache.set(key, { data, timestamp: Date.now() });
}

function getCache<T>(key: string): T | null {
  const cached = cache.get(key);
  if (!cached) return null;
  
  if (Date.now() - cached.timestamp > CACHE_TTL) {
    cache.delete(key);
    return null;
  }
  
  return cached.data;
}

/**
 * Busca o último valor disponível de uma série
 */
export async function getLatestBySeries(seriesCode: number): Promise<IndexPoint | null> {
  const cacheKey = getCacheKey('latest', { seriesCode });
  const cached = getCache<IndexPoint>(cacheKey);
  if (cached) return cached;

  try {
    const { data, error } = await supabase
      .from('economic_indices')
      .select('date, value')
      .eq('series_code', seriesCode)
      .order('date', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.warn(`Erro ao buscar série ${seriesCode}:`, error);
      return await getFallbackLatest(seriesCode);
    }

    const result = data ? { date: data.date, value: data.value } : null;
    if (result) setCache(cacheKey, result);
    return result;

  } catch (error) {
    console.warn(`Erro na busca da série ${seriesCode}:`, error);
    return await getFallbackLatest(seriesCode);
  }
}

/**
 * Busca série em um período específico
 */
export async function getRangeBySeries(
  seriesCode: number, 
  fromDate: string, 
  toDate: string
): Promise<IndexPoint[]> {
  const cacheKey = getCacheKey('range', { seriesCode, fromDate, toDate });
  const cached = getCache<IndexPoint[]>(cacheKey);
  if (cached) return cached;

  try {
    const { data, error } = await supabase
      .from('economic_indices')
      .select('date, value')
      .eq('series_code', seriesCode)
      .gte('date', fromDate)
      .lte('date', toDate)
      .order('date', { ascending: true });

    if (error) {
      console.warn(`Erro ao buscar período da série ${seriesCode}:`, error);
      return [];
    }

    const result = data?.map(item => ({ date: item.date, value: item.value })) || [];
    setCache(cacheKey, result);
    return result;

  } catch (error) {
    console.warn(`Erro na busca por período da série ${seriesCode}:`, error);
    return [];
  }
}

/**
 * Busca últimos N valores de uma série
 */
export async function getLastNBySeries(seriesCode: number, count: number = 12): Promise<IndexPoint[]> {
  const cacheKey = getCacheKey('lastN', { seriesCode, count });
  const cached = getCache<IndexPoint[]>(cacheKey);
  if (cached) return cached;

  try {
    const { data, error } = await supabase
      .from('economic_indices')
      .select('date, value')
      .eq('series_code', seriesCode)
      .order('date', { ascending: false })
      .limit(count);

    if (error) {
      console.warn(`Erro ao buscar últimos ${count} valores da série ${seriesCode}:`, error);
      return [];
    }

    const result = data?.map(item => ({ date: item.date, value: item.value })).reverse() || [];
    setCache(cacheKey, result);
    return result;

  } catch (error) {
    console.warn(`Erro na busca dos últimos valores da série ${seriesCode}:`, error);
    return [];
  }
}

/**
 * Busca status de todas as séries diretamente da Edge Function
 */
export async function getAllSeriesStatus(): Promise<Record<number, IndexPoint | null>> {
  console.log('📋 [DEBUG] getAllSeriesStatus iniciada (Edge Function)...');
  
  const cacheKey = getCacheKey('allStatus');
  const cached = getCache<Record<number, IndexPoint | null>>(cacheKey);
  if (cached) {
    console.log('📋 [DEBUG] Retornando dados do cache:', cached);
    return cached;
  }

  try {
    console.log('📋 [DEBUG] Chamando Edge Function update-indices...');
    
    const { data, error } = await supabase.functions.invoke('update-indices', {
      body: { reason: 'status' }
    });

    if (error) {
      throw new Error(`Edge Function error: ${error.message}`);
    }

    console.log('📋 [DEBUG] Resposta Edge Function:', data);

    if (!data?.ok || !data.data) {
      throw new Error('Formato de resposta inválido da Edge Function');
    }

    // Converter resposta da Edge Function para formato esperado
    const results: Record<number, IndexPoint | null> = {};
    
    // Mapear códigos SGS para chaves da resposta das 11 séries
    const seriesMap: Record<string, number> = {
      // SELIC (3 variações)
      'SELIC_DIARIA': 11,
      'SELIC_META': 432,          // CORRIGIDO: era 1178
      'SELIC_MENSAL': 4390,
      
      // CDI (2 variações)
      'CDI_DAILY': 12,            // NOVO
      'CDI_MENSAL': 4391,         // NOVO
      
      // IPCA (2 variações)
      'IPCA': 433,
      'IPCA_E': 10764,            // NOVO
      
      // Outros índices
      'INPC': 188,
      'IGPM': 189,                // NOVO
      'TR': 226
    };

    Object.entries(data.data).forEach(([key, value]: [string, any]) => {
      const seriesCode = seriesMap[key];
      if (seriesCode && value?.date && value?.value !== undefined) {
        // Converter data brasileira para ISO se necessário
        let isoDate = value.date;
        if (value.date.includes('/')) {
          const [d, m, y] = value.date.split('/');
          isoDate = `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
        }
        
        // Aplicar guards de proteção específicos por série
        let normalizedValue = value.value;
        
        // SELIC Mensal (4390): evita divisão inadequada
        if (seriesCode === 4390) {
          normalizedValue = normalizeMonthlyPct4390(value.value);
          console.log(`📊 SELIC Mensal 4390: ${value.value}% → ${normalizedValue}%`);
        }
        
        // SELIC Meta (432): validação de range (normalmente 8-20% a.a.)
        if (seriesCode === 432 && (normalizedValue < 5 || normalizedValue > 25)) {
          console.warn(`⚠️ SELIC Meta 432 fora do range esperado: ${normalizedValue}%`);
        }
        
        // CDI: normalmente próximo da SELIC
        if ([12, 4391].includes(seriesCode) && (normalizedValue < 0.01 || normalizedValue > 25)) {
          console.warn(`⚠️ CDI ${seriesCode} fora do range esperado: ${normalizedValue}%`);
        }
        
        results[seriesCode] = {
          date: isoDate,
          value: normalizedValue
        };
      }
    });

    console.log('📋 [DEBUG] Resultado final getAllSeriesStatus:', results);
    setCache(cacheKey, results);
    return results;

  } catch (error) {
    console.warn('❌ [DEBUG] Erro ao buscar da Edge Function, tentando fallback:', error);
    
    // Fallback para dados estáticos se Edge Function falhar
    const seriesCodes = Object.keys(ECONOMIC_SERIES).map(Number);
    const results: Record<number, IndexPoint | null> = {};
    
    for (const code of seriesCodes) {
      const fallback = await getFallbackLatest(code);
      results[code] = fallback;
    }
    
    return results;
  }
}

/**
 * Força atualização de séries via Edge Function
 */
export async function updateIndices(seriesCodes: number[], mode: 'daily' | 'monthly' | 'manual' = 'manual'): Promise<any> {
  console.log(`🔧 [DEBUG] updateIndices chamado:`, { seriesCodes, mode });
  
  try {
    console.log(`🔧 [DEBUG] Invocando Edge Function update-indices...`);
    
    // Usa supabase.functions.invoke (sem subrotas)
    const { data, error } = await supabase.functions.invoke('update-indices', {
      body: { series: seriesCodes, mode }
    });

    if (error) {
      throw new Error(`Edge Function error: ${error.message}`);
    }

    console.log(`🔧 [DEBUG] Resposta Edge Function:`, { data });

    // Não usar cache.clear() - deixar para o Admin invalidar via React Query
    console.log(`🔧 [DEBUG] Update concluído, retornando dados:`, data);
    
    return data;

  } catch (error) {
    console.error('❌ [DEBUG] Erro ao atualizar índices:', error);
    throw error;
  }
}

/**
 * Fallback: busca no arquivo estático local (para desenvolvimento/emergência)
 */
async function getFallbackLatest(seriesCode: number): Promise<IndexPoint | null> {
  try {
    // Tenta carregar dados do arquivo estático
    const response = await fetch('/indices_economicos.json');
    if (!response.ok) return null;
    
    const fallbackData = await response.json();
    const seriesData = fallbackData[seriesCode.toString()];
    
    if (!seriesData || !Array.isArray(seriesData) || seriesData.length === 0) {
      return null;
    }

    // Pega o último valor e normaliza
    const latest = seriesData[seriesData.length - 1];
    const [day, month, year] = latest.data.split('/');
    const isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    const value = parseFloat(latest.valor.replace(',', '.'));

    return { date: isoDate, value };

  } catch (error) {
    console.warn(`Erro no fallback para série ${seriesCode}:`, error);
    return null;
  }
}

/**
 * Formata valor para exibição (0.82 -> "0,82%")
 */
export function formatPercentage(value: number): string {
  return `${value.toFixed(2).replace('.', ',')}%`;
}

/**
 * Formata data para exibição brasileira
 */
export function formatDate(isoDate: string): string {
  const date = new Date(isoDate + 'T00:00:00');
  return date.toLocaleDateString('pt-BR', { 
    month: 'short', 
    year: 'numeric' 
  });
}

/**
 * Valida se índice é SELIC (3 variações para regra de negócio)
 */
export function isSelicSeries(seriesCode: number): boolean {
  return [11, 432, 4390].includes(seriesCode); // Over, Meta, Mensal
}